import React from "react";
import { InlineMath, BlockMath } from "react-katex";

import MaturaFunctionGraph from "../../../../components/GraphPlotter/MaturaFunctionGraph";
import MaturaPiecewiseInequalityExplanationGraph from "./MaturaPiecewiseInequalityExplanationGraph";
import IntervalInput from "../../../../components/inputs/IntervalInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";


import {
  randInt,
  parseSingleInterval,
  intervalHasInvalidOrder,
  getIntervalRaw,
  intervalObjectToLatex,
  formatLinear,
} from "./helpers/MaturaPiecewiseFunctionUtils";

const TOL = 1e-9;

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function sameInterval(A, B) {
  return (
    A &&
    B &&
    Math.abs(A.left - B.left) < TOL &&
    Math.abs(A.right - B.right) < TOL &&
    !!A.leftClosed === !!B.leftClosed &&
    !!A.rightClosed === !!B.rightClosed
  );
}

function inInterval(x, I) {
  if (!I) return false;
  if (x > I.left + TOL && x < I.right - TOL) return true;
  if (Math.abs(x - I.left) < TOL && I.leftClosed) return true;
  if (Math.abs(x - I.right) < TOL && I.rightClosed) return true;
  return false;
}

function evalPiece(data, idx, x) {
  if (idx === 1) return data.a1 * x + data.b1;
  if (idx === 2) return data.a2 * x + data.b2;
  return data.a3 * x + data.b3;
}

function valueOfF(data, x) {
  if (inInterval(x, data.interval1)) return evalPiece(data, 1, x);
  if (inInterval(x, data.interval2)) return evalPiece(data, 2, x);
  if (inInterval(x, data.interval3)) return evalPiece(data, 3, x);
  return null;
}

function mirrorIntervalOY(I) {
  if (!I) return null;
  return {
    left: -I.right,
    right: -I.left,
    leftClosed: I.rightClosed,
    rightClosed: I.leftClosed,
  };
}

function applyFlipOX(base) {
  const d = deepClone(base);
  d.a1 = -base.a1;
  d.b1 = -base.b1;
  d.a2 = -base.a2;
  d.b2 = -base.b2;
  d.a3 = -base.a3;
  d.b3 = -base.b3;
  d.cLevel = -base.cLevel;
  return d;
}

function applyFlipOY(base) {
  const d = deepClone(base);
  d.interval1 = mirrorIntervalOY(base.interval3);
  d.interval2 = mirrorIntervalOY(base.interval2);
  d.interval3 = mirrorIntervalOY(base.interval1);

  d.a1 = -base.a3;
  d.b1 = base.b3;
  d.a2 = -base.a2;
  d.b2 = base.b2;
  d.a3 = -base.a1;
  d.b3 = base.b1;
  return d;
}

function pieceRange(a, b, I) {
  const yL = a * I.left + b;
  const yR = a * I.right + b;

  if (Math.abs(yL - yR) < TOL) {
    return {
      left: yL,
      right: yR,
      leftClosed: true,
      rightClosed: true,
    };
  }

  const inc = yL < yR;
  return {
    left: inc ? yL : yR,
    right: inc ? yR : yL,
    leftClosed: inc ? I.leftClosed : I.rightClosed,
    rightClosed: inc ? I.rightClosed : I.leftClosed,
  };
}

function mergeIntervals(intervals) {
  if (!intervals.length) return [];
  const sorted = [...intervals].sort((A, B) =>
    A.left !== B.left ? A.left - B.left : A.right - B.right
  );
  const out = [];
  let cur = { ...sorted[0] };

  for (let i = 1; i < sorted.length; i++) {
    const nxt = sorted[i];
    const separate =
      nxt.left > cur.right + TOL ||
      (Math.abs(nxt.left - cur.right) < TOL &&
        !cur.rightClosed &&
        !nxt.leftClosed);

    if (separate) {
      out.push(cur);
      cur = { ...nxt };
      continue;
    }

    if (nxt.right > cur.right + TOL) {
      cur.right = nxt.right;
      cur.rightClosed = nxt.rightClosed;
    } else if (Math.abs(nxt.right - cur.right) < TOL) {
      cur.rightClosed = cur.rightClosed || nxt.rightClosed;
    }
  }
  out.push(cur);
  return out;
}

function computeRange(data) {
  const pieces = [
    pieceRange(data.a1, data.b1, data.interval1),
    pieceRange(data.a2, data.b2, data.interval2),
    pieceRange(data.a3, data.b3, data.interval3),
  ];
  const merged = mergeIntervals(pieces);
  return merged;
}

function computeEquationIntervals(data, level) {
  const pieces = [
    { a: data.a1, b: data.b1, I: data.interval1 },
    { a: data.a2, b: data.b2, I: data.interval2 },
    { a: data.a3, b: data.b3, I: data.interval3 },
  ];

  const out = [];
  for (const { a, b, I } of pieces) {
    if (Math.abs(a) < TOL) {
      if (Math.abs(b - level) < TOL) out.push({ ...I });
      continue;
    }
    const x = (level - b) / a;
    if (inInterval(x, I)) {
      out.push({
        left: x,
        right: x,
        leftClosed: true,
        rightClosed: true,
      });
    }
  }
  return mergeIntervals(out);
}

function computeSignSplit(data) {
  const roots = [];
  const pieces = [
    { a: data.a1, b: data.b1, I: data.interval1 },
    { a: data.a2, b: data.b2, I: data.interval2 },
    { a: data.a3, b: data.b3, I: data.interval3 },
  ];
  for (const { a, b, I } of pieces) {
    if (Math.abs(a) < TOL) continue;
    const z = -b / a;
    if (inInterval(z, I)) roots.push(z);
  }
  if (!roots.length) return null;
  roots.sort((u, v) => u - v);
  const z = roots[0];

  const L = data.domain.left;
  const R = data.domain.right;
  const leftProbe = (L + z) / 2;
  const fLeft = valueOfF(data, leftProbe);
  if (fLeft == null || Math.abs(fLeft) < TOL) return null;

  const leftPos = fLeft > 0;
  const positive = leftPos
    ? { left: L, right: z, leftClosed: data.domain.leftClosed, rightClosed: false }
    : { left: z, right: R, leftClosed: false, rightClosed: data.domain.rightClosed };
  const negative = leftPos
    ? { left: z, right: R, leftClosed: false, rightClosed: data.domain.rightClosed }
    : { left: L, right: z, leftClosed: data.domain.leftClosed, rightClosed: false };

  return { positive, negative };
}

function buildPoints(data) {
  const x1 = data.interval1.left;
  const x2 = data.interval1.right;
  const x3 = data.interval2.right;
  const x4 = data.interval3.right;

  const eps = 0;
  const markerShift = 0;
  const f = (x) => valueOfF(data, x);

  const y1 = evalPiece(data, 1, x1);
  const y2 = evalPiece(data, 1, x2); // = evalPiece(data,2,x2)
  const y3 = evalPiece(data, 2, x3); // = evalPiece(data,3,x3)
  const y4 = evalPiece(data, 3, x4);

  const y2Right = evalPiece(data, 2, x2);
  const y3Right = evalPiece(data, 3, x3);
  const continuousAtX2 = Math.abs(y2 - y2Right) < TOL;
  const continuousAtX3 = Math.abs(y3 - y3Right) < TOL;

  const x1Line = x1 + eps;
  const x2LineLeft = continuousAtX2 ? x2 : x2 - eps;
  const x2LineRight = continuousAtX2 ? x2 : x2 + eps;
  const x3LineLeft = continuousAtX3 ? x3 : x3 - eps;
  const x3LineRight = continuousAtX3 ? x3 : x3 + eps;
  const x4Line = x4 - eps;

  return {
    A: {
      x: x1,
      y: y1,
      markerSymbol: data.interval1.leftClosed ? "circle-filled" : "circle-open",
      showLabel: false,
    },
    AL: {
      x: x1Line,
      y: evalPiece(data, 1, x1Line),
      showDot: false,
      showMarker: false,
      showLabel: false,
      label: "",
    },

    Bline: {
      x: x2LineLeft,
      y: evalPiece(data, 1, x2LineLeft),
      showDot: false,
      showMarker: false,
      showLabel: false,
      label: "",
    },
    B: {
      x: x2 - (continuousAtX2 ? 0 : markerShift),
      y: y2,
      markerSymbol: data.interval1.rightClosed ? "circle-filled" : "circle-open",
      showMarker: !continuousAtX2,
      showDot: !continuousAtX2,
      showLabel: false,
    },
    B2: {
      x: x2 + (continuousAtX2 ? 0 : markerShift),
      y: y2,
      markerSymbol: data.interval2.leftClosed ? "circle-filled" : "circle-open",
      showMarker: !continuousAtX2,
      showDot: !continuousAtX2,
      showLabel: false,
    },
    BR: {
      x: x2LineRight,
      y: evalPiece(data, 2, x2LineRight),
      showDot: false,
      showMarker: false,
      showLabel: false,
      label: "",
    },

    Cline: {
      x: x3LineLeft,
      y: evalPiece(data, 2, x3LineLeft),
      showDot: false,
      showMarker: false,
      showLabel: false,
      label: "",
    },
    C: {
      x: x3 - (continuousAtX3 ? 0 : markerShift),
      y: y3,
      markerSymbol: data.interval2.rightClosed ? "circle-filled" : "circle-open",
      showMarker: !continuousAtX3,
      showDot: !continuousAtX3,
      showLabel: false,
    },
    C2: {
      x: x3 + (continuousAtX3 ? 0 : markerShift),
      y: y3,
      markerSymbol: data.interval3.leftClosed ? "circle-filled" : "circle-open",
      showMarker: !continuousAtX3,
      showDot: !continuousAtX3,
      showLabel: false,
    },
    CR: {
      x: x3LineRight,
      y: evalPiece(data, 3, x3LineRight),
      showDot: false,
      showMarker: false,
      showLabel: false,
      label: "",
    },

    Dline: {
      x: x4Line,
      y: evalPiece(data, 3, x4Line),
      showDot: false,
      showMarker: false,
      showLabel: false,
      label: "",
    },
    D: {
      x: x4,
      y: y4,
      markerSymbol: data.interval3.rightClosed ? "circle-filled" : "circle-open",
      showLabel: false,
    },
  };
}

function rawIntervalToLatex(raw) {
  return String(raw || "")
    .trim()
    .replace(/[−–—]/g, "-")
    .replace(/;/g, ",")
    .replace(/\s*(?:∪|U)\s*/g, " \\cup ");
}

function renderSingleInterval(raw) {
  if (!raw || !raw.trim()) return <em>(brak odpowiedzi)</em>;
  const I = parseSingleInterval(raw);
  if (!I) return <InlineMath math={rawIntervalToLatex(raw)} />;
  return <InlineMath math={intervalObjectToLatex(I)} />;
}

function parseSingle(raw) {
  if (!raw || !raw.trim()) return null;
  return parseSingleInterval(raw);
}

function generateMay2025OriginalTask() {
  const data = {
    a1: 1,
    b1: 5,
    a2: 0,
    b2: 3,
    a3: -3,
    b3: 9,
    cLevel: 3,
    interval1: { left: -4, right: -2, leftClosed: true, rightClosed: true },
    interval2: { left: -2, right: 2, leftClosed: false, rightClosed: true },
    interval3: { left: 2, right: 4, leftClosed: false, rightClosed: false },
    domain: { left: -4, right: 4, leftClosed: true, rightClosed: false },
    range: { left: -3, right: 3, leftClosed: false, rightClosed: true },
    equationExpected: {
      left: -2,
      right: 2,
      leftClosed: true,
      rightClosed: true,
    },
    signType: "positive",
    signExpected: {
      left: -4,
      right: 3,
      leftClosed: true,
      rightClosed: false,
    },
    maxPoints: 4,
  };
  data.points = buildPoints(data);

  return {
    type: "MaturaPiecewiseFunctionContinuousOpenTask",
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 11,
    },
    data,
  };
}

export function generateTask(options = {}) {
  if (options.variant === "original" && options.session === "2025_MAJ") {
    return generateMay2025OriginalTask();
  }

  for (let attempt = 0; attempt < 700; attempt++) {
    const x1 = randInt(-5, -4);
    const x2 = randInt(-3, -2);
    const x3 = randInt(1, 2);
    const x4 = randInt(x3 + 2, 5);
    if (!(x1 < x2 && x2 < x3 && x3 < x4)) continue;

    const c = [2, 3, 4][randInt(0, 2)];
    const a1 = [1, 2][randInt(0, 1)];
    const b1 = c - a1 * x2;
    const a3 = -[1, 2][randInt(0, 1)];
    const b3 = c - a3 * x3;

    const y1 = a1 * x1 + b1;
    const y4 = a3 * x4 + b3;
    const z = -b3 / a3;

    if (!(y1 > 0 && y1 <= 5)) continue;
    if (Math.abs(z - Math.round(z)) > TOL) continue;
    if (!(z > x3 + TOL && z < x4 - TOL)) continue;
    if (!(y4 >= -5 && y4 < 0)) continue;

    const base = {
      a1,
      b1,
      a2: 0,
      b2: c,
      a3,
      b3,
      cLevel: c,
      interval1: { left: x1, right: x2, leftClosed: true, rightClosed: true },
      interval2: { left: x2, right: x3, leftClosed: false, rightClosed: true },
      interval3: { left: x3, right: x4, leftClosed: false, rightClosed: false },
    };

    const variant = randInt(0, 3);
    let data = deepClone(base);
    if (variant === 1) data = applyFlipOY(data);
    if (variant === 2) data = applyFlipOX(data);
    if (variant === 3) data = applyFlipOY(applyFlipOX(data));

    data.domain = {
      left: data.interval1.left,
      right: data.interval3.right,
      leftClosed: data.interval1.leftClosed,
      rightClosed: data.interval3.rightClosed,
    };

    const rangeMerged = computeRange(data);
    if (rangeMerged.length !== 1) continue;
    data.range = rangeMerged[0];

    const eqMerged = computeEquationIntervals(data, data.cLevel);
    if (eqMerged.length !== 1) continue;
    data.equationExpected = eqMerged[0];

    const signSplit = computeSignSplit(data);
    if (!signSplit) continue;

    data.signType = randInt(0, 1) === 0 ? "positive" : "negative";
    data.signExpected =
      data.signType === "positive" ? signSplit.positive : signSplit.negative;

    data.points = buildPoints(data);
    data.maxPoints = 4;

    return { type: "MaturaPiecewiseFunctionContinuousOpenTask", data };
  }
  return null;
}

export function renderTask({ data }) {
  const {
    a1,
    b1,
    a2,
    b2,
    a3,
    b3,
    cLevel,
    interval1,
    interval2,
    interval3,
    signType,
    points,
  } = data;

  const piece1 = formatLinear(a1, b1);
  const piece2 = Math.abs(a2) < TOL ? String(b2) : formatLinear(a2, b2);
  const piece3 = formatLinear(a3, b3);

  const signPrompt = (
    <>
      Zbiorem argumentów, dla których funkcja <InlineMath math={"f"} /> przyjmuje
      wartości {signType === "positive" ? "dodatnie" : "ujemne"}, jest przedział
    </>
  );

  const sizePx = 350;

  return (
    <div className="flex flex-col gap-3">
      <p>
        Funkcja <InlineMath math={"f"} /> jest określona następująco:
      </p>

      <BlockMath
        math={`f(x)=
\\begin{cases}
${piece1}, & \\text{dla } x\\in ${intervalObjectToLatex(interval1)},\\\\
${piece2}, & \\text{dla } x\\in ${intervalObjectToLatex(interval2)},\\\\
${piece3}, & \\text{dla } x\\in ${intervalObjectToLatex(interval3)}.
\\end{cases}`}
      />

      <div style={{ margin: "0 auto", maxWidth: sizePx }}>
        <MaturaFunctionGraph
          width={sizePx}
          points={points}
          segments={[
            { p1: "AL", p2: "Bline" },
            { p1: "BR", p2: "Cline" },
            { p1: "CR", p2: "Dline" },
          ]}
          xRange={[-6, 6]}
          yRange={[-6, 6]}
        />
      </div>

      <p>
        Uzupełnij zdania. Wpisz odpowiednie przedziały w wykropkowanych miejscach,
        aby zdania były prawdziwe.
      </p>

      <ol style={{ paddingLeft: 20 }}>
        <li>
          Dziedziną funkcji <InlineMath math={"f"} /> jest przedział{" "}
          <InlineMath math={"\\_\\_\\_\\_"} />.
        </li>
        <li>
          Zbiorem wartości funkcji <InlineMath math={"f"} /> jest przedział{" "}
          <InlineMath math={"\\_\\_\\_\\_"} />.
        </li>
        <li>
          {signPrompt} <InlineMath math={"\\_\\_\\_\\_"} />.
        </li>
        <li>
          Zbiorem wszystkich rozwiązań równania{" "}
          <InlineMath math={`f(x)=${cLevel}`} /> jest przedział{" "}
          <InlineMath math={"\\_\\_\\_\\_"} />.
        </li>
      </ol>
    </div>
  );
}

export function renderInput(task, userInput = {}, onChange) {
  const ui = userInput && typeof userInput === "object" ? userInput : {};
  const base = userInput && typeof userInput === "object" ? userInput : {};
  const set = (key) => (value) => onChange({ ...base, [key]: value });

  const signType = task?.data?.signType || "positive";
  const cLevel = task?.data?.cLevel ?? 0;

  return (
    <div className="flex flex-col gap-2">
      <OpenTaskScoringHint variant="independent" />
      <div>
        <strong>1.</strong> Dziedzina:{" "}
        <IntervalInput value={ui.D ?? ""} onChange={set("D")} allowUnion={false} />
      </div>
      <div>
        <strong>2.</strong> Zbiór wartości:{" "}
        <IntervalInput value={ui.R ?? ""} onChange={set("R")} allowUnion={false} />
      </div>
      <div>
        <strong>3.</strong>{" "}
        {signType === "positive"
          ? "Argumenty dla wartości dodatnich:"
          : "Argumenty dla wartości ujemnych:"}{" "}
        <IntervalInput value={ui.S ?? ""} onChange={set("S")} allowUnion={false} />
      </div>
      <div>
        <strong>4.</strong> Rozwiązania równania{" "}
        <InlineMath math={`f(x)=${cLevel}`} />:{" "}
        <IntervalInput value={ui.E ?? ""} onChange={set("E")} allowUnion={false} />
      </div>
    </div>
  );
}

export function checkAnswer(task, userInput = {}) {
  const { domain, range, signExpected, equationExpected, maxPoints } = task.data;

  const rawD = getIntervalRaw(userInput, "D");
  const rawR = getIntervalRaw(userInput, "R");
  const rawS = getIntervalRaw(userInput, "S");
  const rawE = getIntervalRaw(userInput, "E");

  const d = parseSingle(rawD);
  const r = parseSingle(rawR);
  const s = parseSingle(rawS);
  const e = parseSingle(rawE);

  const parts = {
    domain: !!d && sameInterval(d, domain),
    range: !!r && sameInterval(r, range),
    sign: !!s && sameInterval(s, signExpected),
    equation: !!e && sameInterval(e, equationExpected),
  };

  const score = Object.values(parts).filter(Boolean).length;

  const invalidOrderLabels = [];
  if (rawD && intervalHasInvalidOrder(rawD)) invalidOrderLabels.push("dziedzina");
  if (rawR && intervalHasInvalidOrder(rawR)) invalidOrderLabels.push("zbiór wartości");
  if (rawS && intervalHasInvalidOrder(rawS))
    invalidOrderLabels.push("zbiór argumentów");
  if (rawE && intervalHasInvalidOrder(rawE))
    invalidOrderLabels.push("rozwiązania równania");

  const message =
    invalidOrderLabels.length > 0
      ? `Uwaga: w polu/polach ${invalidOrderLabels.join(
          ", "
        )} przedział zapisano od większej do mniejszej liczby. Zapisuj przedziały od mniejszej do większej (lewy koniec ≤ prawy koniec).`
      : null;

  return {
    score,
    maxScore: maxPoints,
    parts,
    message,
    messageType: message ? "hint" : null,
  };
}

export function renderAnswer({ data }) {
  const { domain, range, signExpected, equationExpected } = data;

  return (
    <div className="flex flex-col gap-1" style={{ textAlign: "left" }}>
      <div>
        <strong>1.</strong> Dziedzina: <InlineMath math={intervalObjectToLatex(domain)} />
      </div>
      <div>
        <strong>2.</strong> Zbiór wartości: <InlineMath math={intervalObjectToLatex(range)} />
      </div>
      <div>
        <strong>3.</strong> Zbiór argumentów:{" "}
        <InlineMath math={intervalObjectToLatex(signExpected)} />
      </div>
      <div>
        <strong>4.</strong> Rozwiązania równania:{" "}
        <InlineMath math={intervalObjectToLatex(equationExpected)} />
      </div>
    </div>
  );
}

export function renderUserAnswer(userInput = {}) {
  const rawD = getIntervalRaw(userInput, "D");
  const rawR = getIntervalRaw(userInput, "R");
  const rawS = getIntervalRaw(userInput, "S");
  const rawE = getIntervalRaw(userInput, "E");

  return (
    <div className="flex flex-col gap-1" style={{ textAlign: "left" }}>
      <div>
        <strong>1.</strong> Dziedzina: {renderSingleInterval(rawD)}
      </div>
      <div>
        <strong>2.</strong> Zbiór wartości: {renderSingleInterval(rawR)}
      </div>
      <div>
        <strong>3.</strong> Zbiór argumentów: {renderSingleInterval(rawS)}
      </div>
      <div>
        <strong>4.</strong> Rozwiązania równania: {renderSingleInterval(rawE)}
      </div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const {
    a1,
    b1,
    a2,
    b2,
    a3,
    b3,
    cLevel,
    interval1,
    interval2,
    interval3,
    domain,
    range,
    signType,
    signExpected,
    equationExpected,
    points,
  } = data;

  const piece1 = formatLinear(a1, b1);
  const piece2 = Math.abs(a2) < TOL ? String(b2) : formatLinear(a2, b2);
  const piece3 = formatLinear(a3, b3);
  const signLabel = signType === "positive" ? "wartości dodatnich" : "wartości ujemnych";
  const signOperator = signType === "positive" ? ">" : "<";

  return (
    <div className="flex flex-col gap-2" style={{ textAlign: "left" }}>
      <p>Rozpatrujemy funkcję odcinkową:</p>
      <BlockMath
        math={`f(x)=
\\begin{cases}
${piece1}, & \\text{dla } x\\in ${intervalObjectToLatex(interval1)},\\\\
${piece2}, & \\text{dla } x\\in ${intervalObjectToLatex(interval2)},\\\\
${piece3}, & \\text{dla } x\\in ${intervalObjectToLatex(interval3)}.
\\end{cases}`}
      />

      <p>
        <strong>Krok 1. Dziedzina.</strong> Dziedzina to wszystkie argumenty{" "}
        <InlineMath math={"x"} />, dla których funkcja jest określona,
        czyli cała „rozpiętość” wykresu na osi <InlineMath math={"OX"} />.
      </p>
      <BlockMath math={`D_f=${intervalObjectToLatex(domain)}`} />
      <p>
        <strong>Krok 2. Zbiór wartości.</strong> Zbiór wartości to wszystkie
        wartości <InlineMath math={"y"} />, które przyjmuje funkcja. Odczytujemy
        go z wykresu jako przedział na osi <InlineMath math={"OY"} />.
      </p>
      <BlockMath math={`W_f=${intervalObjectToLatex(range)}`} />
      <p>
        <strong>Krok 3. Argumenty dla {signLabel}.</strong> Oś{" "}
        <InlineMath math={"OX"} /> jest prostą <InlineMath math={"y=0"} />.
        Odczytujemy argumenty, dla których wykres leży{" "}
        {signType === "positive" ? "nad" : "pod"} tą prostą.
      </p>
      <MaturaPiecewiseInequalityExplanationGraph
        points={points}
        segments={[
          { p1: "AL", p2: "Bline" },
          { p1: "BR", p2: "Cline" },
          { p1: "CR", p2: "Dline" },
        ]}
        solutionIntervals={signExpected}
        threshold={0}
        operator={signOperator}
      />
      <BlockMath math={`x\\in ${intervalObjectToLatex(signExpected)}`} />
      <p>
        <strong>Krok 4. Równanie</strong> <InlineMath math={`f(x)=${cLevel}`} />.
        Szukamy argumentów, dla których wykres leży na prostej{" "}
        <InlineMath math={`y=${cLevel}`} />.
      </p>
      <BlockMath math={`x\\in ${intervalObjectToLatex(equationExpected)}`} />
    </div>
  );
}
